import { AlignJustify, LogOut, Settings, ChevronRight } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useClerk } from "@clerk/clerk-react";

function Sidebar() {
  const [isExpanded, setIsExpanded] = useState(window.innerWidth > 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const location = useLocation();
  const clerk = useClerk();

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };

  // Adjust sidebar on window resize
  useEffect(() => {
    const handleResize = () => {
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isSmallScreen);
      setIsExpanded(!isSmallScreen);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Dispatch custom event on sidebar toggle change
  useEffect(() => {
    window.dispatchEvent(
        new CustomEvent("sidebarToggle", { detail: { expanded: isExpanded } })
    );
  }, [isExpanded]);

  // Color palette
  const colors = {
    primary: "#154c79",
    secondary: "#0a2640",
    accent: "#2d7fc1",
    light: "#e9f2fb",
    dark: "#0a1622",
    white: "#ffffff",
  };

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    try {
      await clerk.signOut();
      window.location.href = "/LandingPage";
    } catch (error) {
      console.error("Error during signOut:", error);
      window.location.href = "/LandingPage";
    }
  };

  return (
      <div
          style={{
            fontFamily: "Montserrat, sans-serif",
            backgroundColor: colors.dark,
            boxShadow: "0 0 15px rgba(0,0,0,0.2)",
          }}
          className={`h-full fixed top-0 left-0 transition-all duration-300 ease-in-out flex flex-col justify-between z-40 ${
              isExpanded ? "w-64" : "w-16"
          }`}
      >
        {/* Sidebar Header */}
        <div className="flex flex-col mt-3">
          <div className="flex items-center justify-between px-5 mt-5">
            {isExpanded && (
                <h1 className="text-white text-xl font-semibold tracking-wide">
                  Department
                </h1>
            )}
            <AlignJustify
                onClick={toggleSidebar}
                className="text-white cursor-pointer hover:scale-110 transition-transform duration-200"
            />
          </div>
          {/* User Profile */}
          {isExpanded && (
              <div
                  className="relative flex items-center mx-0 mt-6 px-2 h-14 rounded-r-full w-60"
                  style={{
                    background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  }}
              >
                <h4 className="text-white px-1 flex-1 text-center font-medium">
                  John Doe
                </h4>
                <div className="ml-auto relative">
                  <img
                      src="/images/person.jpg"
                      alt="Profile"
                      className="w-12 h-12 border-2 rounded-full transition-all duration-300 hover:shadow-lg"
                      style={{ borderColor: colors.light }}
                  />
                </div>
              </div>
          )}
          {/* Sidebar Navigation */}
          <nav className="mt-10 flex flex-col items-center space-y-1">
            {[
              { name: "Election", path: "/Elections", icon: ChevronRight },
              { name: "Voters", path: "/Voters", icon: ChevronRight },
              { name: "Candidates", path: "/Candidates", icon: ChevronRight },
            ].map((item, index) => (
                <div key={index} className="relative w-full group">
                  <Link
                      to={item.path}
                      className={`flex items-center px-4 py-3 text-white transition-all duration-200 ${
                          isActive(item.path)
                              ? "bg-opacity-90"
                              : "hover:bg-opacity-30 hover:translate-x-1"
                      }`}
                      style={{
                        backgroundColor: isActive(item.path)
                            ? colors.primary
                            : "transparent",
                        borderRight: isActive(item.path)
                            ? `4px solid ${colors.accent}`
                            : "none",
                      }}
                  >
                    {!isExpanded && (
                        <item.icon className="mx-auto text-white w-5 h-5" />
                    )}
                    {isExpanded && (
                        <>
                          <item.icon className="mr-3 text-white w-5 h-5 opacity-70" />
                          <span className="relative font-medium">
                      {item.name}
                            <span
                                className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300"
                                style={{ backgroundColor: colors.accent }}
                            ></span>
                    </span>
                        </>
                    )}
                  </Link>
                </div>
            ))}
          </nav>
        </div>
        {/* Bottom Section */}
        <div className="flex flex-col items-center mb-5 space-y-4">
          <Link to="/SettingsPage">
            <Settings className="text-white w-6 h-6 cursor-pointer transition-all duration-300 hover:text-sky-300 hover:scale-110 hover:rotate-45" />
          </Link>
          {isExpanded && (
              <div className="w-40 h-0.5 shadow-md" style={{ backgroundColor: colors.accent }}></div>
          )}
          <LogOut
              onClick={handleLogout}
              className="text-white w-6 h-6 cursor-pointer transition-all duration-300 hover:text-red-300 hover:scale-110"
          />
        </div>
      </div>
  );
}

export default Sidebar;
